import React, { useEffect, useState } from "react";
import { Box, InputBase, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import {
  SELECTED_BG,
  SUBJECTS,
  DECISION_TEXT,
  STATUS_HELP,
  STATUS_ORDER,
  SENDER_TYPES,
  ErrorLine,
  HelpTip,
  StatusPill,
  TextAction,
  decisionColor,
  humanizeLabel,
  pal,
  senderLabel,
  shortAgo,
  tierColor,
  usePal,
} from "./reviewUi";
import { isCleanLabel } from "./caseText";
import Filters, { NO_FILTERS, hasFilters } from "./Filters";

// Left inbox: header + stats, tabs, Pending sub-tabs, exact-id search, type filter,
// violation / date filters, case rows.

const TABS = [
  { key: "pending", label: "Pending", stat: "pending" },
  { key: "suspended", label: "Suspended", stat: "suspended" },
  { key: "resolved", label: "Resolved" },
];

// Pending sub-tabs (?sev=), one per worst open decision: Blocked = block,
// Review = review, Uncertain = checker unsure ("report" only).
const SEV_TABS = [
  { key: "blocked", ...STATUS_HELP.blockedTab, stat: "flagged", empty: "No blocked accounts waiting." },
  { key: "review", ...STATUS_HELP.reviewTab, stat: "review", empty: "Nothing waiting for review." },
  { key: "uncertain", ...STATUS_HELP.uncertainTab, stat: "uncertain", empty: "No uncertain accounts waiting." },
];
// Older backends have no Review filter: their "flagged" is block + review.
const OLD_SEV_TABS = [
  { key: "blocked", ...STATUS_HELP.blockedReviewTab, stat: "flagged", empty: "No blocked or review accounts waiting." },
  SEV_TABS[2],
];

const useStyles = makeStyles((theme) => {
  const c = pal(theme);
  return {
    head: { padding: "28px 24px 16px" },
    title: { fontSize: 15, fontWeight: 600, letterSpacing: "-.01em", color: c.primary },
    stats: { display: "flex", gap: 18, marginTop: 6, color: c.hint, fontSize: 12, "& b": { color: c.primary, fontWeight: 600 } },
    tabs: { display: "flex", gap: 20, padding: "0 24px", borderBottom: `1px solid ${c.line}` },
    tab: {
      padding: "10px 0",
      color: c.hint,
      fontSize: 12.5,
      cursor: "pointer",
      background: "none",
      border: 0,
      font: "inherit",
      "&:hover": { color: c.text2 },
      "& i": { fontStyle: "normal", color: c.hint, marginLeft: 4, fontWeight: 400 },
    },
    tabOn: { color: c.primary, fontWeight: 600, boxShadow: `inset 0 -2px 0 ${c.primary}`, "&:hover": { color: c.primary } },
    search: {
      margin: "14px 24px 0",
      border: `1px solid ${c.line}`,
      borderRadius: 8,
      background: "#fff",
      padding: "4px 12px",
      fontSize: 13,
      color: c.text,
      "& input::placeholder": { color: c.hint, opacity: 1 },
    },
    filter: { display: "flex", gap: 14, padding: "10px 24px 10px", fontSize: 12 },
    sevs: { display: "flex", gap: 16, padding: "12px 24px 0", fontSize: 12, "& i": { fontStyle: "normal", marginLeft: 4, fontWeight: 400, color: c.hint } },
    titleRow: { display: "flex", alignItems: "baseline", gap: 12 },
    legendBtn: { marginLeft: "auto", fontSize: 12, whiteSpace: "nowrap", textDecoration: "underline dotted", textUnderlineOffset: "3px" },
    legend: { marginTop: 12, padding: "10px 12px", borderRadius: 8, border: `1px solid ${c.line2}`, background: "#fff" },
    legendRow: { fontSize: 12, color: c.text2, lineHeight: 1.45, "& + &": { marginTop: 10 }, "& > div": { marginTop: 3 } },
    legendTab: { color: c.hint },
    filterOpt: {
      cursor: "pointer",
      color: c.hint,
      background: "none",
      border: 0,
      padding: 0,
      font: "inherit",
      "&:hover": { color: c.text2 },
    },
    filterOn: { color: c.primary, fontWeight: 600, "&:hover": { color: c.primary } },
    clearLink: { color: c.primary, textDecoration: "underline dotted", textUnderlineOffset: "3px", "&:hover": { color: c.primary } },
    rows: { flex: 1, minHeight: 0, overflowY: "auto", borderTop: `1px solid ${c.line2}` },
    row: {
      position: "relative",
      display: "grid",
      gridTemplateColumns: "8px minmax(0, 1fr) auto",
      gap: 12,
      alignItems: "baseline",
      padding: "14px 24px",
      borderBottom: `1px solid ${c.line2}`,
      cursor: "pointer",
      outline: "none",
      "&:hover": { background: "rgba(10,71,76,.03)" },
      "&:focus-visible": { background: SELECTED_BG },
    },
    sel: {
      background: SELECTED_BG,
      boxShadow: `inset 3px 0 0 ${c.primary}`,
      "&:hover": { background: SELECTED_BG },
    },
    dot: { width: 7, height: 7, borderRadius: "50%", alignSelf: "start", marginTop: 7 },
    name: { fontWeight: 600, fontSize: 13, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    chips: { display: "flex", gap: 6, marginTop: 4, "& > span": { fontSize: 11 } },
    line: { color: c.text2, fontSize: 12, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    ctx: { color: c.hint, fontSize: 11.5, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    score: { fontVariantNumeric: "tabular-nums", fontSize: 13 },
    note: { padding: "20px 24px", color: c.hint, fontSize: 12.5 },
    pager: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 16px 8px 24px",
      color: c.hint,
      fontSize: 12,
    },
  };
});

const fmtN = (n) => Number(n || 0).toLocaleString("en-IN");

// "3 templates" for one type, "template, ad" for several.
const countsText = (bySubject) => {
  const present = SUBJECTS.filter((s) => (bySubject?.[s.key] || 0) > 0);
  if (!present.length) return "";
  if (present.length === 1) {
    const n = bySubject[present[0].key];
    return `${n} ${n === 1 ? present[0].single : present[0].plural}`;
  }
  return present.map((s) => s.single).join(", ");
};

// Status words for a row's open items: "Held", "Needs a check, FYI".
const statusWords = (items) => {
  const by = items?.byDecision || {};
  const held = items?.held || 0;
  const words = [];
  if (held > 0) words.push(STATUS_HELP.held.short);
  if ((by.block || 0) - held > 0) words.push(STATUS_HELP.block.short);
  if (by.review) words.push(STATUS_HELP.review.short);
  if (by.report) words.push(STATUS_HELP.report.short);
  return words.map((w, i) => (i && w !== "FYI" ? w.toLowerCase() : w)).join(", ");
};

// "Needs a check · 1 template", "Held, FYI · 20 templates · phishing".
const reasonText = (row) => {
  const lead =
    statusWords(row.items) ||
    DECISION_TEXT[row.items?.worstDecision] ||
    (row.suspension ? `Suspended (${row.suspension.scope || "project"})` : "");
  const label = row.lastViolation?.label;
  const harm = label && !isCleanLabel(label) ? humanizeLabel(label).toLowerCase() : "";
  return [lead, countsText(row.items?.bySubject), harm].filter(Boolean).join(" · ") || "No open items";
};

const contextText = (row) => {
  const at = row.lastFlaggedAt || row.suspension?.at;
  const max = row.deviceRisk?.maxAccountsOnDevice || 0;
  const parts = [];
  if (max >= 2) parts.push(`Device shared by ${max} accounts`);
  if (at) parts.push(shortAgo(at));
  return parts.join(" · ");
};

function Row({ row, selected, onOpen }) {
  const s = useStyles();
  const colors = usePal();
  const open = () => onOpen(row.assistantId);
  const held = row.items?.held || 0;
  const ctx = contextText(row);
  // Owner settings, when the list data carries them.
  const sender = SENDER_TYPES.some((t) => t.value === row.senderType);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      className={`${s.row} ${selected ? s.sel : ""}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      <div className={s.dot} style={{ background: decisionColor(colors, row.items?.worstDecision) }} />
      <div style={{ minWidth: 0 }}>
        <div className={s.name} title={row.assistantName || ""}>
          {row.assistantName || "Unnamed project"}
        </div>
        <div className={s.line}>{reasonText(row)}</div>
        <div className={s.ctx}>
          {held > 0 ? <span style={{ color: colors.error }}>{fmtN(held)} held — not sent to Meta</span> : null}
          {held > 0 && ctx ? " · " : null}
          {ctx || (held > 0 ? null : " ")}
        </div>
        {sender || row.whitelisted ? (
          <div className={s.chips}>
            {sender ? (
              <StatusPill status={{ ...STATUS_HELP[row.senderType], label: senderLabel(row.senderType, row.industry), tone: "sender" }} />
            ) : null}
            {row.whitelisted ? <StatusPill status={{ ...STATUS_HELP.whitelisted, tone: "whitelisted" }} /> : null}
          </div>
        ) : null}
      </div>
      <div className={s.score} style={{ color: row.trust ? tierColor(colors, row.trust.tier) : colors.hint }}>
        {row.trust?.score ?? "—"}
      </div>
    </div>
  );
}

export default function Inbox({
  tab,
  onTab,
  sev,
  onSev,
  subject,
  onSubject,
  filters,
  facets,
  onFilters,
  query,
  onQuery,
  stats,
  list,
  page,
  onPage,
  loading,
  error,
  onRetry,
  selectedId,
  onOpen,
}) {
  const s = useStyles();
  const colors = usePal();
  const [input, setInput] = useState(query);
  const [legend, setLegend] = useState(false);
  useEffect(() => setInput(query), [query]);

  const statN = (key) => (stats && stats[key] != null ? stats[key] : null);
  const sevTabs = statN("review") != null ? SEV_TABS : OLD_SEV_TABS;
  const pages = Math.max(1, Math.ceil((list.total || 0) / (list.limit || 20)));
  const from = list.total ? (page - 1) * list.limit + 1 : 0;
  const to = Math.min(list.total || 0, page * list.limit);
  const filtered = tab !== "suspended" && hasFilters(filters);

  let body;
  if (loading) {
    body = Array.from({ length: 6 }).map((_, i) => (
      <Box key={i} className={s.row} style={{ cursor: "default" }}>
        <span />
        <Box>
          <Skeleton height={18} width="55%" />
          <Skeleton height={16} width="75%" />
          <Skeleton height={14} width="40%" />
        </Box>
        <Skeleton height={18} width={18} />
      </Box>
    ));
  } else if (error) {
    body = <ErrorLine message={error} onRetry={onRetry} style={{ padding: "20px 24px" }} />;
  } else if (!list.cases.length) {
    const noMatch = (
      <>
        No accounts match these filters.{" "}
        <button type="button" className={`${s.filterOpt} ${s.clearLink}`} onClick={() => onFilters(NO_FILTERS)}>
          Clear
        </button>
      </>
    );
    body = (
      <div className={s.note}>
        {query
          ? "No account matches that id."
          : filtered
          ? noMatch
          : tab === "pending" && sev
          ? (sevTabs.find((t) => t.key === sev) || sevTabs[0]).empty
          : { pending: "Nothing waiting for review.", suspended: "No suspended accounts.", resolved: "Nothing resolved yet." }[tab]}
      </div>
    );
  } else {
    body = list.cases.map((row) => (
      <Row key={row.assistantId} row={row} selected={row.assistantId === selectedId} onOpen={onOpen} />
    ));
  }

  return (
    <>
      <div className={s.head}>
        <div className={s.titleRow}>
          <div className={s.title}>Spam review</div>
          <button
            type="button"
            aria-expanded={legend}
            className={`${s.filterOpt} ${s.legendBtn}`}
            onClick={() => setLegend((v) => !v)}
          >
            {legend ? "Hide" : "What do these mean?"}
          </button>
        </div>
        <div className={s.stats}>
          <span>
            <b>{statN("pending") != null ? fmtN(statN("pending")) : "–"}</b> pending
          </span>
          {statN("highRisk") != null ? (
            <span>
              <b style={{ color: colors.error }}>{fmtN(statN("highRisk"))}</b> high risk
            </span>
          ) : null}
          <span>
            <b>{statN("suspended") != null ? fmtN(statN("suspended")) : "–"}</b> suspended
          </span>
          {statN("held") > 0 ? (
            <span>
              <b style={{ color: colors.error }}>{fmtN(statN("held"))}</b> held
            </span>
          ) : null}
        </div>
        {legend ? (
          <div className={s.legend}>
            {STATUS_ORDER.map((k) => (
              <div key={k} className={s.legendRow}>
                <StatusPill status={{ ...STATUS_HELP[k], tone: k }} />
                <div>
                  {STATUS_HELP[k].hint} <span className={s.legendTab}>Listed under {STATUS_HELP[k].tab}.</span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <div className={s.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`${s.tab} ${tab === t.key ? s.tabOn : ""}`}
            onClick={() => onTab(t.key)}
          >
            {t.label}
            {t.stat && statN(t.stat) != null ? <i>{fmtN(statN(t.stat))}</i> : null}
          </button>
        ))}
      </div>
      {tab === "pending" && sev ? (
        <div className={s.sevs} role="tablist" aria-label="Pending accounts">
          {sevTabs.map((t) => (
            <HelpTip key={t.key} help={t.help} term={t.term}>
              <button
                type="button"
                role="tab"
                aria-selected={sev === t.key}
                className={`${s.filterOpt} ${sev === t.key ? s.filterOn : ""}`}
                onClick={() => onSev(t.key)}
              >
                {t.label}
                <i>{fmtN(statN(t.stat))}</i>
              </button>
            </HelpTip>
          ))}
        </div>
      ) : null}
      <InputBase
        className={s.search}
        placeholder="Search by project or owner id"
        value={input}
        inputProps={{ "aria-label": "Search by exact project id or owner client id" }}
        onChange={(e) => {
          setInput(e.target.value);
          if (!e.target.value && query) onQuery("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") onQuery(input.trim());
          if (e.key === "Escape") {
            setInput("");
            onQuery("");
          }
        }}
      />
      <div className={s.filter} style={{ visibility: tab === "suspended" ? "hidden" : "visible" }}>
        {[{ key: "", filter: "All" }, ...SUBJECTS].map((o) => (
          <button
            key={o.key || "all"}
            type="button"
            className={`${s.filterOpt} ${subject === o.key ? s.filterOn : ""}`}
            onClick={() => onSubject(o.key)}
          >
            {o.filter}
          </button>
        ))}
      </div>
      {tab !== "suspended" ? <Filters filters={filters} facets={facets} onChange={onFilters} /> : null}
      <div className={s.rows} aria-busy={loading ? "true" : "false"}>
        {body}
      </div>
      {!loading && !error && !query && pages > 1 ? (
        <div className={s.pager}>
          <span>
            {from}–{to} of {fmtN(list.total)}
          </span>
          <span>
            <TextAction disabled={page <= 1} onClick={() => onPage(page - 1)}>
              Previous
            </TextAction>
            <TextAction disabled={page >= pages} onClick={() => onPage(page + 1)}>
              Next
            </TextAction>
          </span>
        </div>
      ) : null}
    </>
  );
}
